/**
 * SAM-544 (N03): a single place to turn an Axios error into a user-facing
 * message, so a 403 from the SAM-538 capability checks (crud's
 * SENSITIVE_TABLES / service-write gate, portalApi's void/refund gate)
 * always reads as a coherent "you can't do this" instead of a generic
 * "Request failed with status code 403" or a blank toast when the backend's
 * error body doesn't have the exact field a caller expected.
 *
 * Backend error bodies are inconsistent about the field name (`error` on
 * the SAM-538 checks, `message` elsewhere) — check both.
 */

import { seasonSaveIssue, formatSeasonSaveIssue } from './pricingErrors';
import { readAdminLang, translate } from '../i18n/admin';

const DEFAULT_FORBIDDEN_MESSAGE = 'You do not have permission to perform this action.';

export const isForbiddenError = (error: any): boolean => error?.response?.status === 403;

/**
 * S-60 / SAM-974: an account action sent `?expected_sub=` and the username now belongs to another account:
 * 409 `{ error: "This account does not belong to this user row", code: "target_mismatch" }`. Nothing was changed;
 * the caller does not retry.
 */
export const TARGET_MISMATCH_MESSAGE = 'This account no longer belongs to this user record, so nothing was changed. Reload the page and open the account again.';

/**
 * The stable error code of an API error. The lambdas answer `code` at the top of the body; some keep it
 * only under `details` (older shape), so read `code ?? details.code`.
 */
export const apiErrorCode = (error: any): string | undefined => {
  const data = error?.response?.data;
  const code = data?.code ?? data?.details?.code;
  return typeof code === 'string' ? code : undefined;
};

export const isTargetMismatch = (error: any): boolean =>
  error?.response?.status === 409 &&
  (apiErrorCode(error) === 'target_mismatch' || error?.response?.data?.error === 'target_mismatch');

/**
 * A `business*` group (or `dashboard`) on an account with no `custom:business_id` leaves it confined to a
 * business it does not have (the crud answers 403 even on its own profile), so samUserManagement refuses it:
 * 409 `business_id_required` (SAM-994). The admin asks for the business first; this is the server's backstop.
 */
export const businessRequiredMessage = (): string => translate(readAdminLang(), 'user.businessRequired');
export const businessRequiredHint = (): string => translate(readAdminLang(), 'user.businessRequiredHint');

export const isBusinessIdRequired = (error: any): boolean =>
  error?.response?.status === 409 && apiErrorCode(error) === 'business_id_required';

/**
 * SAM-981: a pass (invitation) that has ended takes no change, and a few edits are refused with a stable
 * code: 409 `pass_expired`, 400 `invalid_dates`, 409 `guest_idnum_locked`, 409 `use_invitation_endpoint`.
 */
const PASS_ERROR_CODES = ['pass_expired', 'invalid_dates', 'guest_idnum_locked', 'use_invitation_endpoint'] as const;
export type PassErrorCode = (typeof PASS_ERROR_CODES)[number];

export const passErrorCode = (error: any): PassErrorCode | null => {
  const status = error?.response?.status;
  const code = apiErrorCode(error);
  if (status !== 409 && status !== 400) return null;
  return (PASS_ERROR_CODES as readonly string[]).includes(code ?? '') ? (code as PassErrorCode) : null;
};

export const isPassError = (error: any): boolean => passErrorCode(error) !== null;

/** The readable message (active language) for a pass error, or null when the error is not one. */
export const passErrorMessage = (error: any): string | null => {
  const code = passErrorCode(error);
  return code ? translate(readAdminLang(), `passError.${code}` as const) : null;
};

/**
 * SAM-814: cancelling a paid pass voids its charge first. The server refuses with a stable code, at the top of
 * the body (`code`) or, on `POST /business_access/void`, in `message`: 409 `pass_has_payment` (cancelled through
 * crud with a live charge), 409 `void_window_closed` (past the bank cutoff), 400/409 `CANNOT_VOID_CHECKED_IN`,
 * 409 `payment_in_progress`, 503 `PAYMENT_LEDGER_UNAVAILABLE`.
 */
const PASS_PAYMENT_ERROR_CODES = [
  'pass_has_payment', 'void_window_closed', 'CANNOT_VOID_CHECKED_IN', 'payment_in_progress', 'PAYMENT_LEDGER_UNAVAILABLE',
] as const;
export type PassPaymentErrorCode = (typeof PASS_PAYMENT_ERROR_CODES)[number];

export const passPaymentErrorCode = (error: any): PassPaymentErrorCode | null => {
  const status = error?.response?.status;
  if (status !== 409 && status !== 400 && status !== 503) return null;
  const data = error?.response?.data;
  const code = apiErrorCode(error) ?? (typeof data?.message === 'string' ? data.message : undefined);
  return (PASS_PAYMENT_ERROR_CODES as readonly string[]).includes(code ?? '') ? (code as PassPaymentErrorCode) : null;
};

/** The readable message (active language) for a paid-pass cancel error, or null when the error is not one. */
export const passPaymentErrorMessage = (error: any): string | null => {
  const code = passPaymentErrorCode(error);
  return code ? translate(readAdminLang(), `passPayment.${code}` as const) : null;
};

/**
 * SAM-1323: creating a user with an email that already has an account answers 409 `USER_EXISTS_LEGACY`, whose text
 * («the person signs in and is migrated») was written for old accounts and reads wrong for a new employee.
 */
export const USER_EXISTS_MESSAGE = 'This email already has an account. Use another email, or find the person in Users.';

export const isUserExists = (error: any): boolean =>
  error?.response?.status === 409 && apiErrorCode(error) === 'USER_EXISTS_LEGACY';

export const getApiErrorMessage = (error: any, fallback = 'Something went wrong. Please try again.'): string => {
  if (isTargetMismatch(error)) return TARGET_MISMATCH_MESSAGE;
  if (isBusinessIdRequired(error)) return businessRequiredMessage();
  if (isUserExists(error)) return USER_EXISTS_MESSAGE;
  const pass = passErrorMessage(error);
  if (pass) return pass;
  const passPayment = passPaymentErrorMessage(error);
  if (passPayment) return passPayment;
  // SAM-626: a rejected season list has stable codes; the CRUD `error` text ("Seasons overlap")
  // names no seasons, so use the readable copy (the editor names them by label on the rows).
  const season = seasonSaveIssue(error);
  if (season) return formatSeasonSaveIssue({ ...season, ids: [] });
  if (isForbiddenError(error)) {
    return error?.response?.data?.error || error?.response?.data?.message || DEFAULT_FORBIDDEN_MESSAGE;
  }
  return error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;
};


/**
 * Tenant Front Desk contract: cancelling a booking, or a pass tied to a booking, that has a payment answers
 * 409 `booking_has_payment`. Cancelling it would leave the money behind, so an admin must refund or void first.
 */
export const BOOKING_HAS_PAYMENT_MESSAGE = 'This booking has a payment, so it cannot be cancelled here. Ask an administrator to void or refund it first.';

export const isBookingHasPayment = (error: any): boolean =>
  error?.response?.status === 409 &&
  (error?.response?.data?.code === 'booking_has_payment' || error?.response?.data?.error === 'booking_has_payment');
